import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  createEmployee,
  updateEmployee,
  clearError,
} from "../../redux/employeeSlice";
import styles from "./CreateEmployee.module.css";

const initialForm = {
  name: "",
  email: "",
  password: "",
  phone: "",
  department: "",
  designation: "",
  employeeId: "",
  status: "active",
};

export default function CreateEmployee() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { employees, loading, error } = useSelector((state) => state.employees);

  const [form, setForm] = useState(initialForm);
  const [localError, setLocalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const existing = employees.find((e) => e.id === id);
      if (existing) {
        setForm({
          name: existing.name || "",
          email: existing.email || "",
          password: "",
          phone: existing.phone || "",
          department: existing.department || "",
          designation: existing.designation || "",
          employeeId: existing.employeeId || "",
          status: existing.status || "active",
        });
      }
    }
  }, [isEdit, id, employees]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    setLocalError("");
    if (!form.name.trim()) return setLocalError("Full Name is required");
    if (!form.email.trim()) return setLocalError("Email is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return setLocalError("Enter a valid email");
    if (!isEdit && !form.password) return setLocalError("Password is required");
    if (!isEdit && form.password.length < 6)
      return setLocalError("Password must be at least 6 characters");
    if (!form.phone.trim()) return setLocalError("Phone Number is required");
    if (!form.department.trim()) return setLocalError("Department is required");
    if (!form.designation.trim())
      return setLocalError("Designation is required");
    if (!form.employeeId.trim())
      return setLocalError("Employee ID is required");
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    setLocalError("");
    const validationError = validate();
    if (validationError) return;

    setIsSubmitting(true);
    try {
      if (isEdit) {
        const { password, ...updates } = form;
        await dispatch(updateEmployee({ id, updates })).unwrap();
        navigate("/admin/employees");
      } else {
        await dispatch(createEmployee(form)).unwrap();
        navigate("/admin/employees");
      }
    } catch (err) {
      setLocalError(err || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1>{isEdit ? "Edit Employee" : "Create Employee"}</h1>
          <p>
            {isEdit
              ? "Update employee details"
              : "Add a new employee to the system"}
          </p>
        </div>

        {(error || localError) && (
          <div className={styles.alertError}>{error || localError}</div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Employee Name"
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="example@vyrapharma.in"
              />
            </div>

            {!isEdit && (
              <div className={styles.field}>
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min 6 characters"
                />
              </div>
            )}

            <div className={styles.field}>
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="10-digit number"
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="employeeId">Employee ID</label>
              <input
                id="employeeId"
                name="employeeId"
                value={form.employeeId}
                onChange={handleChange}
                placeholder="EMP-001"
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="department">Department</label>
              <input
                id="department"
                name="department"
                value={form.department}
                onChange={handleChange}
                placeholder="Sales"
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="designation">Designation</label>
              <input
                id="designation"
                name="designation"
                value={form.designation}
                onChange={handleChange}
                placeholder="Executive"
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => navigate("/admin/employees")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading
                ? "Saving..."
                : isEdit
                  ? "Update Employee"
                  : "Create Employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
